import { useEffect, useMemo, useRef, useState } from 'react';
import './halloween.css';

// Capa ambiente muy suave + "sustos" puntuales cada cierto tiempo (eventEvery, en segundos)
const PRESETS = {
  ligero: {
    bats: 3,
    embers: 14,
    pumpkins: ['left'],
    webs: ['top-left'],
    mist: 0.15,
    vignette: 0,
    eventEvery: [60, 100],
  },
  completo: {
    bats: 4,
    embers: 22,
    pumpkins: ['left', 'right'],
    webs: ['top-left', 'top-right'],
    mist: 0.22,
    vignette: 0,
    eventEvery: [30, 55],
  },
  oscuro: {
    bats: 6,
    embers: 30,
    pumpkins: ['left', 'right'],
    webs: ['top-left', 'top-right'],
    mist: 0.3,
    vignette: 0.4,
    eventEvery: [20, 40],
  },
};

const EVENTS = {
  swarm: 7000,
  ghost: 6500,
  spider: 8000,
  eyes: 5000,
  witch: 12000,
};
const TRIGGER_EVENT = 'halloween-fx-event';

const random = (min, max) => min + Math.random() * (max - min);

const normalizePreset = (value) => {
  const normalized = (value || '').toString().toLowerCase();
  if (normalized === 'off' || normalized === 'none') {
    return 'off';
  }
  if (PRESETS[normalized]) {
    return normalized;
  }
  return 'completo';
};

// Permite forzar el efecto desde la URL (?halloween=oscuro, ?halloween=off) para pruebas
const getQueryOverride = () => {
  if (typeof window === 'undefined') return null;

  const queryPreset = new URLSearchParams(window.location.search).get('halloween');
  if (!queryPreset) return null;

  const normalized = normalizePreset(queryPreset);
  return {
    enabled: normalized !== 'off',
    preset: normalized === 'off' ? 'completo' : normalized,
  };
};

const usePrefersReducedMotion = () => {
  const [prefers, setPrefers] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefers(mediaQuery.matches);
    const handler = () => setPrefers(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handler);
    } else {
      mediaQuery.addListener(handler);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handler);
      } else {
        mediaQuery.removeListener(handler);
      }
    };
  }, []);

  return prefers;
};

// remoteEnabled llega desde la gestión (socket 'efectos'); la URL o window.HalloweenFX lo sobrescriben
export const useHalloweenController = (remoteEnabled = false) => {
  const [override, setOverride] = useState(() => getQueryOverride());
  const state = override || { enabled: !!remoteEnabled, preset: 'completo' };

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const api = {
      enable: () => setOverride({ ...state, enabled: true }),
      disable: () => setOverride({ ...state, enabled: false }),
      toggle: () => setOverride({ ...state, enabled: !state.enabled }),
      setPreset: (nextPreset) => {
        const normalized = normalizePreset(nextPreset);
        setOverride({
          enabled: normalized !== 'off',
          preset: normalized === 'off' ? 'completo' : normalized,
        });
      },
      reset: () => setOverride(null),
      // Lanza un susto concreto: 'swarm' | 'ghost' | 'spider' | 'eyes' | 'witch'
      trigger: (type) => window.dispatchEvent(new CustomEvent(TRIGGER_EVENT, { detail: type })),
      state: () => ({ ...state }),
    };

    window.HalloweenFX = api;
    return () => {
      delete window.HalloweenFX;
    };
  }, [state.enabled, state.preset]);

  return state;
};

const drawBat = (ctx, x, y, size, flap) => {
  const wing = Math.sin(flap) * size * 0.6;
  ctx.beginPath();
  ctx.moveTo(x, y);
  // ala izquierda
  ctx.quadraticCurveTo(x - size * 0.5, y - size * 0.4 - wing, x - size, y - wing * 0.8);
  ctx.quadraticCurveTo(x - size * 0.75, y + size * 0.05, x - size * 0.6, y + size * 0.2);
  ctx.quadraticCurveTo(x - size * 0.4, y + size * 0.05, x - size * 0.25, y + size * 0.25);
  ctx.quadraticCurveTo(x - size * 0.15, y + size * 0.1, x, y + size * 0.2);
  // ala derecha
  ctx.quadraticCurveTo(x + size * 0.15, y + size * 0.1, x + size * 0.25, y + size * 0.25);
  ctx.quadraticCurveTo(x + size * 0.4, y + size * 0.05, x + size * 0.6, y + size * 0.2);
  ctx.quadraticCurveTo(x + size * 0.75, y + size * 0.05, x + size, y - wing * 0.8);
  ctx.quadraticCurveTo(x + size * 0.5, y - size * 0.4 - wing, x, y);
  ctx.fill();
  // orejas
  ctx.beginPath();
  ctx.moveTo(x - size * 0.08, y);
  ctx.lineTo(x - size * 0.12, y - size * 0.18);
  ctx.lineTo(x, y - size * 0.05);
  ctx.lineTo(x + size * 0.12, y - size * 0.18);
  ctx.lineTo(x + size * 0.08, y);
  ctx.fill();
};

const HalloweenCanvas = ({ bats, embers, swarm }) => {
  const canvasRef = useRef(null);
  const swarmRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext('2d');
    let animationFrame;
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();

    const createBat = (initial) => {
      const direction = Math.random() > 0.5 ? 1 : -1;
      return {
        x: initial ? Math.random() * canvas.width : direction > 0 ? -40 : canvas.width + 40,
        baseY: 30 + Math.random() * canvas.height * 0.35,
        size: random(8, 14),
        speed: random(0.5, 1.1) * direction,
        flap: Math.random() * Math.PI * 2,
        flapSpeed: random(0.18, 0.26),
        wave: Math.random() * Math.PI * 2,
        amplitude: random(10, 30),
        opacity: random(0.35, 0.55),
      };
    };

    const createEmber = (initial) => ({
      x: Math.random() * canvas.width,
      y: initial ? random(canvas.height * 0.5, canvas.height) : canvas.height + 10,
      radius: random(0.8, 2),
      speedY: random(0.25, 0.7),
      drift: (Math.random() - 0.5) * 0.6,
      phase: Math.random() * Math.PI * 2,
      hue: random(20, 45),
    });

    const batsArray = Array.from({ length: bats }, () => createBat(true));
    const embersArray = Array.from({ length: embers }, () => createEmber(true));

    const renderBat = (bat) => {
      bat.wave += 0.02;
      bat.flap += bat.flapSpeed;
      bat.x += bat.speed;
      ctx.globalAlpha = bat.opacity;
      drawBat(ctx, bat.x, bat.baseY + Math.sin(bat.wave) * bat.amplitude, bat.size, bat.flap);
    };

    const render = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // brasas que suben y se apagan antes de llegar arriba
      embersArray.forEach((ember, index) => {
        const progress = 1 - ember.y / canvas.height;
        const flicker = 0.6 + Math.sin(ember.phase * 3) * 0.4;
        ctx.globalAlpha = Math.max(0, (0.8 - progress) * flicker);
        ctx.fillStyle = `hsl(${ember.hue}, 100%, 60%)`;
        ctx.shadowColor = `hsl(${ember.hue}, 100%, 50%)`;
        ctx.shadowBlur = 6;
        ctx.beginPath();
        ctx.arc(ember.x, ember.y, ember.radius, 0, Math.PI * 2);
        ctx.fill();

        ember.phase += 0.02;
        ember.y -= ember.speedY;
        ember.x += Math.sin(ember.phase) * ember.drift;

        if (progress > 0.8) {
          embersArray[index] = createEmber(false);
        }
      });

      // murciélagos: silueta oscura con un halo morado para que se vean sobre fondos oscuros
      ctx.fillStyle = '#140a1c';
      ctx.shadowColor = 'rgba(170, 90, 255, 0.6)';
      ctx.shadowBlur = 6;

      batsArray.forEach((bat, index) => {
        renderBat(bat);
        if (bat.x > canvas.width + 60 || bat.x < -60) {
          batsArray[index] = createBat(false);
        }
      });

      swarmRef.current = swarmRef.current.filter((bat) => {
        renderBat(bat);
        return bat.speed > 0 ? bat.x < canvas.width + 80 : bat.x > -80;
      });

      ctx.shadowBlur = 0;
      ctx.globalAlpha = 1;
      animationFrame = requestAnimationFrame(render);
    };

    render();

    window.addEventListener('resize', resize);
    return () => {
      cancelAnimationFrame(animationFrame);
      window.removeEventListener('resize', resize);
    };
  }, [bats, embers]);

  useEffect(() => {
    if (!swarm) return;
    const width = window.innerWidth;
    const direction = Math.random() > 0.5 ? 1 : -1;
    const groupY = random(0.15, 0.45) * window.innerHeight;

    swarmRef.current = Array.from({ length: 16 }, () => ({
      x: direction > 0 ? random(-400, -40) : random(width + 40, width + 400),
      baseY: groupY + random(-70, 70),
      size: random(12, 24),
      speed: random(4, 6.5) * direction,
      flap: Math.random() * Math.PI * 2,
      flapSpeed: random(0.35, 0.5),
      wave: Math.random() * Math.PI * 2,
      amplitude: random(8, 25),
      opacity: random(0.75, 0.95),
    }));
  }, [swarm]);

  return <canvas ref={canvasRef} className="halloween-canvas" aria-hidden="true" />;
};

const Cobweb = ({ position }) => (
  <svg
    className={`halloween-web halloween-web--${position}`}
    viewBox="0 0 200 200"
    aria-hidden="true"
  >
    <g fill="none" stroke="rgba(230, 230, 240, 0.45)" strokeWidth="1.2">
      <path d="M0 0 L200 40 M0 0 L170 110 M0 0 L110 170 M0 0 L40 200" />
      <path d="M40 8 Q34 22 36 36 Q22 34 8 40" />
      <path d="M80 16 Q68 44 72 70 Q44 68 16 80" />
      <path d="M125 25 Q106 66 110 108 Q66 106 25 125" />
      <path d="M170 34 Q146 90 150 146 Q90 146 34 170" />
    </g>
  </svg>
);

const Pumpkin = ({ side }) => (
  <svg className={`halloween-pumpkin halloween-pumpkin--${side}`} viewBox="0 0 100 90" aria-hidden="true">
    <path d="M48 14 Q50 2 60 0 L62 4 Q54 6 54 16 Z" fill="#4a6b1f" />
    <ellipse cx="30" cy="52" rx="26" ry="34" fill="#d9590a" />
    <ellipse cx="70" cy="52" rx="26" ry="34" fill="#d9590a" />
    <ellipse cx="50" cy="52" rx="24" ry="37" fill="#f07a12" />
    <g className="halloween-pumpkin-face" fill="#ffd84a">
      <path d="M28 40 L40 40 L34 28 Z" />
      <path d="M60 40 L72 40 L66 28 Z" />
      <path d="M46 50 L54 50 L50 43 Z" />
      <path d="M22 58 Q50 80 78 58 L70 62 L66 56 L60 64 L54 57 L48 65 L42 57 L36 64 L31 56 Z" />
    </g>
  </svg>
);

const SpiderIcon = () => (
  <svg className="halloween-spider-icon" viewBox="0 0 40 40" aria-hidden="true">
    <g stroke="#140a1c" strokeWidth="2" fill="none" strokeLinecap="round">
      <path d="M14 18 L6 10 L2 16 M14 21 L4 20 L1 27 M14 24 L6 30 L5 37" />
      <path d="M26 18 L34 10 L38 16 M26 21 L36 20 L39 27 M26 24 L34 30 L35 37" />
    </g>
    <ellipse cx="20" cy="24" rx="7" ry="9" fill="#140a1c" />
    <circle cx="20" cy="13" r="5" fill="#140a1c" />
    <circle cx="18" cy="12" r="1.3" fill="#ff7a1a" />
    <circle cx="22" cy="12" r="1.3" fill="#ff7a1a" />
  </svg>
);

const GhostBody = () => (
  <span className="halloween-ghost-body">
    <span className="halloween-ghost-eye" />
    <span className="halloween-ghost-eye" />
    <span className="halloween-ghost-mouth" />
  </span>
);

const WitchIcon = () => (
  <svg className="halloween-witch-icon" viewBox="0 0 140 90" aria-hidden="true">
    <g transform="translate(0 10)">
      <path d="M10 52 L128 44" stroke="#3b2414" strokeWidth="3" strokeLinecap="round" />
      <path d="M14 52 L0 42 L4 50 L0 56 L6 58 L0 64 L16 56 Z" fill="#7a4a1c" />
      <g fill="#140a1c">
        <path d="M62 48 Q44 28 26 38 Q40 42 46 52 Z" />
        <path d="M56 48 L70 18 L86 46 Z" />
        <circle cx="74" cy="17" r="6" />
        <path d="M66 12 L84 11 L70 -6 Z" />
      </g>
      <path d="M61 13 L89 11" stroke="#140a1c" strokeWidth="3" strokeLinecap="round" />
      <path d="M76 44 L88 52 L94 49" stroke="#140a1c" strokeWidth="3" fill="none" strokeLinecap="round" />
    </g>
  </svg>
);

// Cada susto se coloca con valores aleatorios fijados al crearse (event.*)
const HalloweenEvent = ({ event }) => {
  switch (event.type) {
    case 'ghost':
      return (
        <div
          className={`halloween-peek halloween-peek--${event.side}`}
          style={{ bottom: `${event.y}%` }}
        >
          <div className="halloween-peek-inner">
            <GhostBody />
          </div>
        </div>
      );
    case 'spider':
      return (
        <div className="halloween-drop" style={{ left: `${event.x}%`, '--drop': `${event.depth}vh` }}>
          <span className="halloween-drop-thread" />
          <SpiderIcon />
        </div>
      );
    case 'eyes':
      return (
        <div
          className="halloween-eyes"
          style={{ [event.side]: `${event.x}%`, top: `${event.y}%` }}
        >
          <span className="halloween-eye" />
          <span className="halloween-eye" />
        </div>
      );
    case 'witch':
      return (
        <div
          className={`halloween-witch${event.side === 'right' ? ' halloween-witch--reverse' : ''}`}
          style={{ top: `${event.y}%` }}
        >
          <WitchIcon />
        </div>
      );
    default:
      return null;
  }
};

const createEvent = (type) => {
  const side = Math.random() > 0.5 ? 'left' : 'right';
  return {
    type,
    key: Date.now(),
    side,
    x: type === 'eyes' ? random(3, 14) : random(15, 85),
    y: type === 'witch' ? random(6, 18) : type === 'eyes' ? random(55, 78) : random(20, 55),
    depth: random(22, 40),
  };
};

const useHalloweenEvents = (eventEvery, enabled) => {
  const [event, setEvent] = useState(null);

  useEffect(() => {
    if (!enabled) return undefined;

    const types = Object.keys(EVENTS);
    let lastType = null;
    let nextTimeout;
    let clearTimeoutId;

    const fire = (type) => {
      lastType = type;
      clearTimeout(clearTimeoutId);
      setEvent(createEvent(type));
      clearTimeoutId = setTimeout(() => setEvent(null), EVENTS[type]);
    };

    const schedule = (delay) => {
      nextTimeout = setTimeout(() => {
        const options = types.filter((type) => type !== lastType);
        fire(options[Math.floor(Math.random() * options.length)]);
        schedule(random(eventEvery[0], eventEvery[1]) * 1000);
      }, delay);
    };

    const onTrigger = (e) => {
      if (EVENTS[e.detail]) fire(e.detail);
    };

    schedule(random(5, 12) * 1000);
    window.addEventListener(TRIGGER_EVENT, onTrigger);
    return () => {
      clearTimeout(nextTimeout);
      clearTimeout(clearTimeoutId);
      window.removeEventListener(TRIGGER_EVENT, onTrigger);
    };
  }, [eventEvery, enabled]);

  return event;
};

const Mist = ({ opacity }) => (
  <div className="halloween-mist" style={{ opacity }} aria-hidden="true">
    <div className="halloween-mist-layer" />
  </div>
);

const HalloweenFX = ({ preset = 'completo' }) => {
  const reduceMotion = usePrefersReducedMotion();
  const config = PRESETS[preset] || PRESETS.completo;
  const event = useHalloweenEvents(config.eventEvery, !reduceMotion);
  const swarm = useMemo(() => (event?.type === 'swarm' ? event.key : null), [event]);

  return (
    <div className="halloween-wrapper" aria-hidden="true">
      {config.vignette > 0 && <div className="halloween-vignette" style={{ opacity: config.vignette }} />}
      <Mist opacity={config.mist} />
      {!reduceMotion && <HalloweenCanvas bats={config.bats} embers={config.embers} swarm={swarm} />}
      {config.webs.map((position) => (
        <Cobweb key={position} position={position} />
      ))}
      {config.pumpkins.map((side) => (
        <Pumpkin key={side} side={side} />
      ))}
      {event && event.type !== 'swarm' && <HalloweenEvent key={event.key} event={event} />}
    </div>
  );
};

export default HalloweenFX;
